'use client';

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  QuestionMarkCircledIcon,
} from '@radix-ui/react-icons';

import { QueryOrdering } from '@/api/generated/model';
import { Button } from '@/comps/primitives/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/comps/primitives/select';
import { cn } from '@/utils/shad';
import { jetbrainsMono } from '@/theme/utils/font.utils';
import React, { PropsWithChildren, ReactNode, useCallback, useMemo, useState } from 'react';
import { TableHead } from './primitives/table';
import { TooltipButton } from './tooltip-button';
import { SetStateParam, useQueryParamStateV2 } from '@/utils/use-query-param-state';
import type { Options } from 'nuqs';
import { TableHeadContent } from '@/comps/table-head-content';
import { ThreeDotsMenu, UIPopoverMenuItemType } from './component-library/list/ThreeDotsMenu';

import { SortableButton } from '@/comps/sortable-table-head';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/comps/primitives/tooltip';

type Variant = 'default' | 'simple';

type PaginationProps = {
  pageSize: number;
  setPageIndex: (pageIndex: number) => void;
  pageIndex: number;
  totalPages: number;
  className?: string;
  /** No outer px/gap (e.g. when the parent already provides spacing). */
  flush?: boolean;
  enableRowsPerPage?: boolean;
  setPageSize?: (pageSize: number) => void;
  possibleSizes: number[];
  onPageChange?: (pageIndex: number) => void;
  variant?: Variant;
  showTotalPages?: boolean;
  /** When true, always render with disabled controls and a count placeholder. */
  loading?: boolean;
};

export function Pagination({
  pageSize,
  setPageIndex,
  pageIndex,
  totalPages,
  className,
  flush,
  enableRowsPerPage,
  possibleSizes,
  setPageSize,
  onPageChange,
  variant = 'default',
  showTotalPages = true,
  loading = false,
}: PaginationProps) {
  if (totalPages <= 1 && !loading) {
    return null;
  }

  const navDisabled = loading || pageIndex === 0;
  const nextDisabled = loading || pageIndex === totalPages - 1;
  const firstLastDisabled = loading || pageIndex === 0;
  const lastDisabled = loading || pageIndex === totalPages - 1;

  return (
    <div
      className={cn(
        jetbrainsMono.className,
        'flex items-center justify-between text-[12px]',
        flush ? 'gap-0 px-0' : 'gap-2 px-2',
        className,
      )}
    >
      <div className={cn('flex items-center', flush ? 'gap-2' : 'space-x-2 lg:space-x-4')}>
        {variant === 'default' && (
          <>
            {enableRowsPerPage && (
              <div className="flex items-center space-x-2">
                <p className="font-medium">Rows per page</p>
                <Select
                  value={pageSize.toString()}
                  disabled={loading}
                  onValueChange={(value) => {
                    setPageSize?.(parseInt(value));
                    onPageChange?.(parseInt(value));
                  }}
                >
                  <SelectTrigger className="h-8 w-17.5 text-[12px]">
                    <SelectValue placeholder={pageSize} />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {possibleSizes.map((pageSize) => (
                      <SelectItem key={pageSize} value={`${pageSize}`}>
                        {pageSize}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </>
        )}
        {showTotalPages && (
          <div className="flex w-25 min-w-max items-center justify-center font-medium text-muted-foreground">
            {loading ? 'Page — of —' : `Page ${pageIndex + 1} of ${totalPages}`}
          </div>
        )}
        <div className="flex items-center gap-x-2">
          {variant === 'default' && (
            <Button
              variant="ghost"
              disableHoverEffect
              className="hidden h-8 w-8 border-0 p-0 text-[12px] shadow-none lg:flex"
              onClick={(ev) => {
                ev.stopPropagation();
                onPageChange?.(0);
                setPageIndex(0);
              }}
              disabled={firstLastDisabled}
            >
              <DoubleArrowLeftIcon className="h-3 w-3" />
            </Button>
          )}
          <Button
            variant="ghost"
            disableHoverEffect
            className="h-8 w-8 border-0 p-0 text-[12px] shadow-none"
            onClick={(ev) => {
              ev.stopPropagation();
              onPageChange?.(pageIndex - 1);
              setPageIndex(pageIndex - 1);
            }}
            disabled={navDisabled}
          >
            <ChevronLeftIcon className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            disableHoverEffect
            className="h-8 w-8 border-0 p-0 text-[12px] shadow-none"
            onClick={(ev) => {
              ev.stopPropagation();
              onPageChange?.(pageIndex + 1);
              setPageIndex(pageIndex + 1);
            }}
            disabled={nextDisabled}
          >
            <ChevronRightIcon className="h-3 w-3" />
          </Button>
          {variant === 'default' && (
            <Button
              variant="ghost"
              disableHoverEffect
              className="hidden h-8 w-8 border-0 p-0 text-[12px] shadow-none lg:flex"
              onClick={(ev) => {
                ev.stopPropagation();
                onPageChange?.(totalPages - 1);
                setPageIndex(totalPages - 1);
              }}
              disabled={lastDisabled}
            >
              <DoubleArrowRightIcon className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export const DEFAULT_PAGE_SIZE = 10;
const pageSizes = [10, 20, 30, 40, 50];

export type UsePaginationReturnType = {
  setPageIndex: (params: SetStateParam<number>, options?: Options) => void;
  calcTotalPages: (totalItems?: number) => number;
  pageIndex: number;
  offset: number;
  pageSize: number;
  possibleSizes: number[];
  reset: () => void;
  setPageSize: (params: SetStateParam<number>, options?: Options) => void;
};

export const usePagination = (defaultPageSize = 10, possibleSizes?: number[]): UsePaginationReturnType => {
  const [internalPaginationKey] = useState(Math.random());

  const [pageSize, _setLimit] = useQueryParamStateV2(`pageSize-${internalPaginationKey}`, defaultPageSize, {
    type: 'objectORnumberORboolean',
  });
  const setLimit = useCallback(
    (params: SetStateParam<number>, options?: Options) => {
      _setLimit(params, {
        history: 'push',
        ...options,
      });
    },
    [_setLimit],
  );

  const [pageIndex, _setPageIndex] = useQueryParamStateV2(`pageIndex-${internalPaginationKey}`, 0, {
    type: 'objectORnumberORboolean',
  });
  const setPageIndex = useCallback(
    (params: SetStateParam<number>, options?: Options) => {
      _setPageIndex(params, {
        history: 'push',
        ...options,
      });
    },
    [_setPageIndex],
  );

  const calcTotalPages = useCallback((totalItems?: number) => Math.ceil((totalItems || 1) / pageSize), [pageSize]);

  const reset = useCallback(() => {
    _setLimit(defaultPageSize, { history: 'replace' });
    _setPageIndex(0, { history: 'replace' });
  }, [_setLimit, _setPageIndex, defaultPageSize]);

  return {
    setPageIndex,
    calcTotalPages,
    pageIndex,
    offset: pageIndex * pageSize,
    pageSize,
    setPageSize: setLimit,
    possibleSizes: possibleSizes || pageSizes,
    reset,
  };
};

export const useLimitManager = (defaultLimit: number, possibleLimits: number[]) => {
  const [limit, setLimit] = React.useState(defaultLimit);

  return { limit, setLimit, possibleLimits };
};

type BaseSortBy = { by?: string; direction?: 'asc' | 'desc' };

export const SortableHeadV2 = <TSort extends BaseSortBy = QueryOrdering>(
  props: PropsWithChildren & {
    title: string;
    subtitle?: string;
    tooltip?: string;
    titleTooltip?: string;
    truncateTitle?: boolean;
    sortBy?: TSort;
    setSortBy?: (q: TSort) => void;
    orderKey: TSort['by'];
    totalsChip?: React.ReactNode;
    className?: string;
    icon?: ReactNode;
    leadingControl?: ReactNode;
    total?: number;
    showTotal?: boolean;
    actions?: UIPopoverMenuItemType[];
    isEdited?: boolean;
    style?: React.CSSProperties;
  },
) => {
  const nextSort = useMemo(() => {
    if (props.orderKey === props.sortBy?.by) {
      return { by: props.orderKey, direction: props.sortBy?.direction === 'asc' ? 'desc' : 'asc' } as TSort;
    }
    return { by: props.orderKey, direction: 'desc' } as TSort;
  }, [props.orderKey, props.sortBy?.by, props.sortBy?.direction]);

  const titleTooltipText = props.titleTooltip ?? props.title;

  const renderTitleContent = () => {
    const headContent = (
      <TableHeadContent
        title={props.title}
        total={props.total ?? 0}
        showTotal={props.showTotal ?? false}
        truncate={props.truncateTitle}
      />
    );

    if (!props.truncateTitle) {
      return headContent;
    }

    return (
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <span className="min-w-0 overflow-hidden">{headContent}</span>
        </TooltipTrigger>
        <TooltipContent>{titleTooltipText}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TableHead
      className={cn(
        'text-medium w-46 pr-4 pl-3 text-nowrap text-muted-foreground underline-offset-2 hover:cursor-pointer hover:underline',
        props.truncateTitle && 'overflow-hidden',
        props.className,
      )}
      onClick={(ev) => {
        ev.stopPropagation();
        props.setSortBy?.(nextSort);
      }}
      style={props.style}
    >
      {props.children}
      <div className={cn('flex flex-row items-center gap-2', props.truncateTitle && 'min-w-0')}>
        {props.leadingControl}
        <span
          className={cn(
            'csv-header flex items-center text-[12px]',
            props.truncateTitle ? 'min-w-0 flex-1 overflow-hidden' : undefined,
          )}
          title={props.truncateTitle ? undefined : props.title}
        >
          {props.icon && <span className="mr-1 shrink-0">{props.icon}</span>}
          {renderTitleContent()}
          <span className="shrink-0 text-[8px] text-darkgrey">{props.subtitle}</span>
          {props.sortBy && (
            <span className="shrink-0">
              <SortableButton
                value={props.orderKey ?? ''}
                currentSort={props.sortBy.by ?? ''}
                direction={props.sortBy.direction}
              />
            </span>
          )}
          {props.tooltip && (
            <TooltipButton content={props.tooltip}>
              <QuestionMarkCircledIcon className="ml-1 inline h-base w-base text-muted-foreground hover:cursor-pointer" />
            </TooltipButton>
          )}
          {props.totalsChip}
        </span>
        {props.actions && (
          <ThreeDotsMenu
            align="start"
            iconProps={{
              className: 'text-muted-foreground hover:text-accent-foreground',
              size: 14,
            }}
            items={props.actions}
          />
        )}
      </div>
    </TableHead>
  );
};
